import { useEffect, useState } from 'react';
import { Settings2, Trash2, Bookmark, Check, Ban } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { EntitySavedViews, ViewPick } from './DashboardToolbar';
import { EntityKey } from '@/components/entities/registry';
import { ColorPreset } from '@/components/operations/types';
import { COLOR_PRESETS, COLOR_PRESET_ORDER } from '@/components/operations/formats';
import { ChartBackground } from './dashboardViews';

interface ColumnEditorProps {
  title: string;
  titleIsOverride: boolean;
  entityKey: EntityKey;
  viewId: string;
  color: ColorPreset | null;
  /** When true, exposes chart-specific options like background fill. */
  isChartView: boolean;
  chartBg: ChartBackground;
  entityViews: EntitySavedViews[];
  onChangeView: (pick: ViewPick) => void;
  onChangeTitle: (title: string | undefined) => void;
  onChangeColor: (color: ColorPreset | null) => void;
  onChangeChartBg: (bg: ChartBackground) => void;
  onDelete: () => void;
}

export function ColumnEditor({
  title,
  titleIsOverride,
  entityKey,
  viewId,
  color,
  isChartView,
  chartBg,
  entityViews,
  onChangeView,
  onChangeTitle,
  onChangeColor,
  onChangeChartBg,
  onDelete,
}: ColumnEditorProps) {
  const [open, setOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState(titleIsOverride ? title : '');

  useEffect(() => {
    if (open) setDraftTitle(titleIsOverride ? title : '');
  }, [open, title, titleIsOverride]);

  const commitTitle = () => {
    const trimmed = draftTitle.trim();
    onChangeTitle(trimmed ? trimmed : undefined);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="opacity-0 group-hover:opacity-100 flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-opacity"
          title="Edit column"
        >
          <Settings2 className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="px-3 py-2 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-900">Edit column</div>
        </div>

        <div className="px-3 py-2.5 border-b border-slate-100">
          <label className="block text-[11px] uppercase tracking-wider font-medium text-slate-400 mb-1">
            Title
          </label>
          <Input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                commitTitle();
                setOpen(false);
              }
            }}
            placeholder={title}
            className="h-8 text-sm"
          />
          <div className="text-[11px] text-slate-400 mt-1">
            Leave blank to use the saved view's name.
          </div>
        </div>

        <div className="px-3 py-2.5 border-b border-slate-100">
          <label className="block text-[11px] uppercase tracking-wider font-medium text-slate-400 mb-1.5">
            Color theme
          </label>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => onChangeColor(null)}
              className={cn(
                'w-6 h-6 rounded-md border flex items-center justify-center text-slate-400',
                color === null
                  ? 'border-slate-900 ring-1 ring-slate-900'
                  : 'border-slate-200 hover:border-slate-300'
              )}
              title="No color"
            >
              <Ban className="w-3.5 h-3.5" />
            </button>
            {COLOR_PRESET_ORDER.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onChangeColor(c)}
                className={cn(
                  'w-6 h-6 rounded-md border',
                  COLOR_PRESETS[c].swatch,
                  color === c
                    ? 'ring-2 ring-offset-1 ring-slate-900'
                    : 'hover:opacity-80'
                )}
                title={c}
              />
            ))}
          </div>
        </div>

        {isChartView && (
          <div className="px-3 py-2.5 border-b border-slate-100">
            <label className="block text-[11px] uppercase tracking-wider font-medium text-slate-400 mb-1.5">
              Chart background
            </label>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => onChangeChartBg('white')}
                className={cn(
                  'w-6 h-6 rounded-md border bg-white',
                  chartBg === 'white'
                    ? 'border-slate-900 ring-1 ring-slate-900'
                    : 'border-slate-300 hover:border-slate-400'
                )}
                title="White"
              />
              <button
                type="button"
                onClick={() => onChangeChartBg('transparent')}
                className={cn(
                  'w-6 h-6 rounded-md border',
                  'bg-[linear-gradient(45deg,#cbd5e1_25%,transparent_25%),linear-gradient(-45deg,#cbd5e1_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#cbd5e1_75%),linear-gradient(-45deg,transparent_75%,#cbd5e1_75%)]',
                  'bg-[length:8px_8px] bg-[position:0_0,0_4px,4px_-4px,-4px_0]',
                  chartBg === 'transparent'
                    ? 'border-slate-900 ring-1 ring-slate-900'
                    : 'border-slate-300 hover:border-slate-400'
                )}
                title="Transparent"
              />
              {COLOR_PRESET_ORDER.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onChangeChartBg(c)}
                  className={cn(
                    'w-6 h-6 rounded-md border',
                    COLOR_PRESETS[c].swatch,
                    chartBg === c
                      ? 'ring-2 ring-offset-1 ring-slate-900'
                      : 'hover:opacity-80'
                  )}
                  title={c}
                />
              ))}
            </div>
          </div>
        )}

        <div className="px-1 py-1.5 border-b border-slate-100">
          <div className="px-2 py-1 text-[11px] uppercase tracking-wider font-medium text-slate-400">
            Use saved view
          </div>
          <div className="max-h-56 overflow-y-auto">
            {entityViews.every((e) => e.views.length === 0) ? (
              <div className="px-3 py-3 text-xs text-slate-400 text-center">
                No saved views
              </div>
            ) : (
              entityViews.map(({ entity, views }) =>
                views.length === 0 ? null : (
                  <div key={entity.key} className="mb-1">
                    <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                      {entity.label}
                    </div>
                    {views.map((v) => {
                      const active = entityKey === entity.key && viewId === v.id;
                      return (
                        <button
                          key={v.id}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm hover:bg-slate-50 text-left',
                            active && 'bg-slate-50 font-medium'
                          )}
                          onClick={() => {
                            onChangeView({ entityKey: entity.key, viewId: v.id });
                            setOpen(false);
                          }}
                        >
                          {active ? (
                            <Check className="w-3.5 h-3.5 text-slate-900 shrink-0" />
                          ) : (
                            <span className="w-3.5 h-3.5 shrink-0" />
                          )}
                          <Bookmark className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="text-slate-900 truncate">{v.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )
              )
            )}
          </div>
        </div>

        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700 h-8 text-sm"
            onClick={() => {
              onDelete();
              setOpen(false);
            }}
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" />
            Delete column
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
